import { NavLink, useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Brand, SiteFooter } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { DefaultPasswordBanner } from "@/components/ChangePassword";
import { FeedbackButton } from "@/components/FeedbackButton";

const NAV = [
  ["/app", "Catalog", "nav-catalog"],
  ["/app/batch", "Review fixes", "nav-review"],
  ["/app/proof", "Proof", "nav-proof"],
  ["/app/settings", "Settings", "nav-settings"],
];

export const AppShell = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = user?.role === "admin" ? [...NAV, ["/admin", "Admin", "nav-admin"]] : NAV;
  const onLogout = async () => {
    await logout();
    navigate("/login");
  };
  return (
    <div className="min-h-screen flex flex-col">
      <DefaultPasswordBanner />
      <header className="relative z-30 border-b border-white/[0.06] bg-[#07080F]/60 backdrop-blur-xl">
        <div aria-hidden="true" className="gold-line absolute inset-x-0 top-0" />
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-4 sm:px-8">
          <Brand to="/app" />
          <nav className="hidden md:flex items-center gap-1">
            {items.map(([to, label, id]) => (
              <NavLink
                key={to}
                to={to}
                end
                data-testid={id}
                className={({ isActive }) =>
                  `relative inline-flex min-h-[44px] items-center rounded-full px-4 text-sm transition-colors after:absolute after:inset-x-4 after:-bottom-[13px] after:h-px after:bg-gradient-to-r after:from-transparent after:via-[#E9C98B] after:to-transparent after:transition-opacity ${isActive ? "bg-white/[0.08] text-foreground shadow-[inset_0_0_0_1px_rgba(255,255,255,0.1)] after:opacity-100" : "text-muted-foreground hover:text-foreground after:opacity-0"}`
                }
              >
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <span data-testid="current-user-email" className="hidden lg:block text-sm text-muted-foreground">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={onLogout} data-testid="logout-button" className="min-w-[44px] rounded-full">
              <LogOut className="h-4 w-4" />
              <span className="sr-only sm:not-sr-only sm:ml-1">Log out</span>
            </Button>
          </div>
        </div>
        <nav className="md:hidden grid grid-cols-3 gap-1.5 px-5 pb-3">
          {items.map(([to, label, id]) => (
            <NavLink
              key={to}
              to={to}
              end
              data-testid={`${id}-mobile`}
              className={({ isActive }) =>
                `flex min-h-[44px] items-center justify-center truncate rounded-full px-2 text-center text-[13px] ${isActive ? "bg-white/[0.1] text-foreground shadow-[inset_0_0_0_1px_rgba(233,201,139,0.45)]" : "bg-white/[0.03] text-muted-foreground"}`
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="mx-auto w-full max-w-7xl flex-1 px-5 py-10 sm:px-8 sm:py-14">{children}</main>
      <SiteFooter />
      <div aria-hidden="true" className="h-20" />
      <FeedbackButton />
    </div>
  );
};

export const PageTitle = ({ eyebrow, title, children, testid }) => (
  <div className="mb-10 flex flex-col gap-5 md:flex-row md:items-end md:justify-between fade-up">
    <div>
      {eyebrow && <p className="eyebrow">{eyebrow}</p>}
      <h1 data-testid={testid} className="mt-3 text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">{title}</h1>
    </div>
    {children && <div className="flex flex-wrap gap-2">{children}</div>}
  </div>
);
